import type { MaybeFunction } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { useSurface } from '@sohanscript/ui/dialog';
import { Heading } from '@sohanscript/ui/heading';
import { PoweredBy } from '@sohanscript/ui/powered-by';
import { Suspense, useEffect, useRef } from 'react';
import { z } from 'zod';
import { Cta } from '#/components/cta.tsx';
import { ExperienceSection } from '#/components/experiences/exp-section.tsx';
import { MainHero } from '#/components/main-hero.tsx';
import { About } from '@/components/about';
import { Contact } from '@/components/contact';
import { NotesPreview } from '@/components/notes/notes-preview';
import { Stack } from '@/components/stack';
import { Testimonials } from '@/components/testimonials';
import { ProjectSlider } from '@/components/works/project-slider';
import { useClickMockStore } from '@/lib/store/click-mock.ts';
import { getBrand } from '../utils/app-env';

const windowSchema = z.array(
	z.object({
		id: z.string(),
		title: z.string(),
		icon: z.string(),
		status: z.enum(['open', 'closed']).default('closed'),
		zIndex: z.number().default(10),
		width: z.number().default(672),
		minWidth: z.number().default(300),
		minHeight: z.number().default(460),
		contentClassName: z.string().optional(),
		/** Center-relative starting offsets. */
		initialPosition: z
			.object({
				left: z.number().optional(),
				right: z.number().optional(),
				top: z.number().optional(),
				bottom: z.number().optional(),
			})
			.optional(),
		element: z
			.custom<MaybeFunction<React.ReactElement>>()
			.or(z.string().describe('URL')),
	}),
);

export type WindowConfig = z.input<typeof windowSchema>[number];

const Spinner = () => (
	<div className="flex h-40 items-center justify-center">
		<div className="size-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
	</div>
);

function AboutWindowElement() {
	const surface = useSurface();
	const isFocused = surface?.isFocused ?? false;
	const { setShow } = useClickMockStore();
	const wasFocused = useRef(isFocused);

	// Only dismiss the shared click-mouse mock when the about window *gains*
	// focus (not-focused → focused). Dismissing on every focused render would
	// hide the mock as soon as the window opens, before the user interacts
	// with it. `openWindow` gives the new window top z-index immediately, so
	// the window is focused on its first render too.
	useEffect(() => {
		if (isFocused && !wasFocused.current) {
			setShow(false);
		}
		wasFocused.current = isFocused;
	}, [isFocused, setShow]);

	return (
		<div id="about--container" className="pt-12 pb-4">
			<About className="overflow-clip" />
		</div>
	);
}

export const WINDOW_CONFIGS = windowSchema.parse([
	{
		id: 'go-to-home',
		title: '/home/sohan',
		icon: 'lucide:home',
		element: '/',
	},
	{
		id: 'hero',
		title: `${getBrand().domain}.sh`,
		icon: 'lucide:terminal',
		status: 'closed',
		initialPosition: { left: 200, bottom: 0 },
		contentClassName: 'overflow-hidden',
		minWidth: 460,
		element: (
			<Suspense fallback={<Spinner />}>
				<MainHero bare />
			</Suspense>
		),
	},
	{
		id: 'about',
		title: 'about.md',
		icon: 'solar:user-rounded-linear',
		status: 'closed',
		width: 720,
		contentClassName: 'max-h-[75vh]',
		initialPosition: { right: 200, top: 150 },
		element: <AboutWindowElement />,
	},
	{
		id: 'notes',
		title: 'notes/',
		icon: 'solar:document-add-linear',
		status: 'closed',
		width: 780,
		contentClassName: 'max-h-[75vh]',
		element: (
			<div className="py-12">
				<Heading
					titlePrefix={m.nav_notes()}
					titleHighlight={m.heading_notes_highlight()}
					description={m.notes_sub()}
					className="container mb-12"
					ampersand
				/>
				<Suspense fallback={<Spinner />}>
					<NotesPreview />
				</Suspense>
			</div>
		),
	},
	{
		id: 'skills',
		title: 'stack.json',
		icon: 'ph:stack-duotone',
		status: 'closed',
		width: 1080,
		minWidth: 700,
		contentClassName: 'overflow-clip',
		element: <Stack />,
	},
	{
		id: 'experiences',
		title: 'experiences.md',
		icon: 'lucide:briefcase-business',
		status: 'closed',
		width: 720,
		contentClassName: 'max-h-[75vh]',
		element: (
			<div className="pt-12 pb-4">
				<ExperienceSection mini />
			</div>
		),
	},
	{
		id: 'works',
		title: 'projects/',
		icon: 'lucide:container',
		status: 'closed',
		width: 1080,
		contentClassName: 'max-h-[80vh] overflow-x-clip',
		element: (
			<div className="overflow-hidden py-12">
				<Heading
					titlePrefix={m.nav_works()}
					titleHighlight={m.heading_works_highlight()}
					description={m.works_header_tagline()}
					ampersand
					className="container mb-12"
				/>
				<Suspense
					fallback={
						<div className="h-130">
							<Spinner />
						</div>
					}
				>
					<ProjectSlider />
				</Suspense>
			</div>
		),
	},
	{
		id: 'cta',
		title: 'action.sh',
		icon: 'icon-park-outline:press',
		status: 'closed',
		width: 800,
		contentClassName: 'max-h-[75vh]',
		element: <Cta noMenuBtn />,
	},
	{
		id: 'testimonials',
		title: 'testimonials.md',
		icon: 'streamline-plump:user-feedback-heart',
		status: 'closed',
		width: 1300,
		contentClassName: 'overflow-clip',
		element: (
			<div className="py-12">
				<Suspense fallback={null}>
					<Testimonials />
				</Suspense>
			</div>
		),
	},
	{
		id: 'powered-by',
		title: 'powered-by.md',
		icon: 'lucide:zap',
		status: 'closed',
		width: 600,
		contentClassName: 'max-h-fit',
		element: (
			<div className="py-12">
				<PoweredBy />
			</div>
		),
	},
	{
		id: 'contact',
		title: 'contact.sh',
		icon: 'mage:message',
		status: 'closed',
		width: 600,
		contentClassName: 'max-h-[75vh]',
		element: (
			<div className="py-12">
				<Contact />
			</div>
		),
	},
] satisfies WindowConfig[]);
